import {
	Button,
	ButtonGroup,
	Card,
	LuIcon,
	Separator,
	stopPropagation,
	Text,
	toast,
	useAlertDialog,
} from "@overmap-ai/blocks"
import {
	type Asset,
	type AssetType,
	type AssetTypeFieldValuesAttachment,
	type AssetTypeStatus,
	EMPTY_ARRAY,
	ERROR_MSG_UNEXPECTED,
	pluralize,
	type ProcedureType,
	type Stored,
} from "@overmap-ai/common"
import {
	selectAssetTypeStatusesOfAssetType,
	selectLatestAssetTypeFieldsOfAssetType,
	selectProcedureTypesOfOrganization,
} from "@overmap-ai/core"
import { extractFilesAndPromisesFromFieldValues, type Field, type FieldValues } from "@overmap-ai/forms"
import * as RadixAccordian from "@radix-ui/react-accordion"
import { memo, useCallback } from "react"
import { v4 as uuidv4 } from "uuid"

import { useConfirmDeleteDialog } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useAppSelector, useShallowEqaulSelector } from "../../store"
import {
	selectActiveProjectId,
	selectAttachmentsByAssetTypeFieldValues,
	selectLatestFieldValuesByAssetOfAssetType,
	selectProcedureInitiativesOfProjectAndAssetType,
} from "../../store/selectors"
import { AssetBulkDropdownMenu } from "../DropdownMenus/AssetBulkDropdownMenu"
import { AssetTypeFieldsSubmitter } from "../Form/AssetTypeFieldsSubmitter"
import { useDeserializeFieldSchema } from "../Form/hooks"
import { getAttachmentPayloadsFromFilesAndPromises, getFieldValuesAttachmentsByFieldId } from "../Form/utils"
import { AssetTypeIcon } from "../Icons/AssetTypeIcon"
import { progressToast } from "../Toasts"
import { BulkAssetControlProcedureInitiativeItem } from "./BulkAssetControlProcedureInitiativeItem"

interface BulkAssetControlAssetTypeItemProps {
	assetType: Stored<AssetType>
	assetIds: Asset["uuid"][]
}

export const BulkAssetControlAssetTypeItem = memo<BulkAssetControlAssetTypeItemProps>((props) => {
	const { assetType, assetIds } = props

	const projectId = useAppSelector(selectActiveProjectId)!
	const latestAssetTypeFields = useAppSelector(selectLatestAssetTypeFieldsOfAssetType(assetType.uuid))
	const latestAssetTypeFieldValuesByAsset = useShallowEqaulSelector(
		selectLatestFieldValuesByAssetOfAssetType(assetType.uuid),
	)
	const attachmentsByAssetTypeFieldValues = useAppSelector(selectAttachmentsByAssetTypeFieldValues)
	const procedureInitiatives = useShallowEqaulSelector(
		selectProcedureInitiativesOfProjectAndAssetType(projectId, assetType.uuid),
	)
	const assetTypeStatuses = useShallowEqaulSelector(selectAssetTypeStatusesOfAssetType(assetType.uuid))
	const procedureTypes = useShallowEqaulSelector(selectProcedureTypesOfOrganization(assetType.organization))
	const openAlertDialog = useAlertDialog()
	const openConfirmDeleteDialog = useConfirmDeleteDialog()

	const fieldSchema = useDeserializeFieldSchema(latestAssetTypeFields?.fields ?? EMPTY_ARRAY)

	const submitAssetTypeFields = useCallback(
		async (allValues: FieldValues) => {
			if (!latestAssetTypeFields?.uuid) return

			if (fieldSchema.areValuesBlank(allValues)) return

			const changedValues = fieldSchema.changedFieldValues(fieldSchema.blankValues(), allValues)

			const progressToastId = uuidv4()
			const progressToastDescription = `Creating submissions for ${assetIds.length} assets.`

			progressToast({
				id: progressToastId,
				description: progressToastDescription,
				progress: 0,
			})

			const [values, filesAndPromises] = extractFilesAndPromisesFromFieldValues(
				fieldSchema.getFields(),
				changedValues,
			)

			const fieldValuesByAsset: Record<Asset["uuid"], FieldValues> = {}
			const fieldValuesAttachmentsByFieldIdByAsset: Record<
				Asset["uuid"],
				Record<Field["id"], Stored<AssetTypeFieldValuesAttachment>[]>
			> = {}

			for (const assetId of assetIds) {
				const assetTypeFieldValues = latestAssetTypeFieldValuesByAsset[assetId]
				const attachments = attachmentsByAssetTypeFieldValues[assetTypeFieldValues?.uuid ?? ""] ?? []

				const fieldValues = fieldSchema.deserializeValues(assetTypeFieldValues?.values ?? {})
				const attachmentsByFieldId = getFieldValuesAttachmentsByFieldId(attachments)

				for (const fieldId in values) {
					if (fieldId in fieldValues) delete fieldValues[fieldId]
					if (fieldId in attachmentsByFieldId) delete attachmentsByFieldId[fieldId]
				}

				fieldValuesByAsset[assetId] = fieldValues
				fieldValuesAttachmentsByFieldIdByAsset[assetId] = attachmentsByFieldId
			}

			const [offlineAssetTypeFieldValues, batchPromises] = sdk.assetTypeFieldValues.bulkAdd({
				values: fieldSchema.serializeValues(values),
				payloads: Object.entries(fieldValuesByAsset).map(([assetId, values]) => ({
					asset: assetId,
					values: fieldSchema.serializeValues(values),
					fields_revision: latestAssetTypeFields.uuid,
					published_at: new Date().toISOString(),
				})),
			})

			void sdk.assetTypeFieldValuesAttachments.bulkAdd(
				offlineAssetTypeFieldValues.flatMap(({ uuid, asset }) => {
					const attachmentsByFieldId = fieldValuesAttachmentsByFieldIdByAsset[asset] ?? []
					return getAttachmentPayloadsFromFilesAndPromises(uuid, filesAndPromises, []).concat(
						Object.entries(attachmentsByFieldId).flatMap(([fieldId, attachments]) =>
							attachments.map((attachment) => ({
								field_values: uuid,
								field_identifier: fieldId,
								file_sha1: attachment.file_sha1,
								file_extension: attachment.file_extension,
								file_name: attachment.file_name,
								file_type: attachment.file_type,
								object_url: attachment.object_url,
							})),
						),
					)
				}),
			)

			for (const [index, batchPromise] of batchPromises.entries()) {
				void batchPromise.then(() => {
					progressToast({
						id: progressToastId,
						description: progressToastDescription,
						progress: ((index + 1) / batchPromises.length) * 100,
					})
				})
			}

			void Promise.all(batchPromises)
				.then(() => {
					toast.success({
						title: "Success",
						description: `Fields set on ${assetIds.length} assets.`,
					})
				})
				.catch((error) => {
					toast.error({
						title: "Failed to set fields",
						description: error.message ?? ERROR_MSG_UNEXPECTED,
					})
				})
				.finally(() => {
					toast.dismiss(progressToastId)
				})
		},
		[
			latestAssetTypeFields?.uuid,
			assetIds,
			fieldSchema,
			latestAssetTypeFieldValuesByAsset,
			attachmentsByAssetTypeFieldValues,
		],
	)

	const handleAssetTypeFieldsSubmit = useCallback(
		(values: FieldValues) => {
			openAlertDialog({
				title: "Confirm mass fields submission?",
				description: `You are about to set fields on ${assetIds.length} assets. Are you sure?`,
				size: "sm",
				onAction: () => submitAssetTypeFields(values),
			})
		},
		[openAlertDialog, assetIds.length, submitAssetTypeFields],
	)

	const handleStartInitiative = useCallback(
		(procedureType?: Stored<ProcedureType>) => {
			if (!procedureType) return

			const [offlineProcedureInitiave] = sdk.procedureInitiatives.add({
				procedure_type: procedureType.uuid,
				project: projectId,
				asset_type: assetType.uuid,
			})

			void sdk.procedures.bulkAdd(
				assetIds.map((assetId) => ({
					procedure_type: procedureType.uuid,
					asset: assetId,
					project: projectId,
					issue: null,
					procedure_initiative: offlineProcedureInitiave.uuid,
				})),
			)

			// TODO: Navigate to procedure initiative detail view here?
		},
		[projectId, assetIds, assetType.uuid],
	)

	const handleStatusChange = useCallback(
		(status: Stored<AssetTypeStatus> | undefined) => {
			if (!status) return

			openAlertDialog({
				title: "Confirm mass status change?",
				description: `You are about to set the status to "${status.name}" on ${assetIds.length} assets. Are you sure?`,
				size: "sm",
				onAction: () => {
					sdk.assets.bulkUpdate(assetIds.map((uuid) => ({ uuid, status: status.uuid })))
					toast.success({
						title: "Status updated",
						description: `Status set on ${assetIds.length} assets.`,
					})
				},
			})
		},
		[openAlertDialog, assetIds],
	)

	const handleBulkDelete = useCallback(() => {
		openConfirmDeleteDialog({
			title: `Delete ${pluralize("asset", assetIds.length)}`,
			description: `The ${assetIds.length} ${pluralize("asset", assetIds.length)} will be deleted. This action cannot be undone.`,
			onAction: () => void sdk.assets.bulkDelete(assetIds),
		})
	}, [openConfirmDeleteDialog, assetIds])

	return (
		<RadixAccordian.Item value={assetType.uuid} asChild>
			<Card
				className="hover:inset-ring-(--base-a7) flex flex-col gap-4 transition-[box-shadow]"
				role="listitem"
				variant="outline"
				size="sm"
			>
				<RadixAccordian.Trigger asChild>
					<ButtonGroup
						className="group flex w-full cursor-pointer items-center justify-between gap-2"
						tabIndex={0}
						size="sm"
						variant="ghost"
						accentColor="base"
					>
						<div className="flex min-w-0 items-center gap-2">
							<AssetTypeIcon assetTypeId={assetType.uuid} />
							<Text className="truncate">{assetType.name}</Text>
							<Text accentColor="base" weight="regular" size="sm">
								{assetIds.length} selected
							</Text>
						</div>
						<div className="flex items-center justify-end gap-2">
							<AssetBulkDropdownMenu
								onDelete={handleBulkDelete}
								statuses={assetTypeStatuses}
								onStatusSelect={handleStatusChange}
								procedureTypes={procedureTypes}
								onProcedureTypeSelect={handleStartInitiative}
								size="sm"
							>
								<Button size="sm" variant="soft" accentColor="base" onClick={stopPropagation}>
									<LuIcon icon={modelIconMapping.command} />
									Actions
								</Button>
							</AssetBulkDropdownMenu>
						</div>
					</ButtonGroup>
				</RadixAccordian.Trigger>

				<RadixAccordian.Content className="flex flex-col gap-4">
					{!!latestAssetTypeFields && (
						<>
							<Separator size="full" />
							<AssetTypeFieldsSubmitter
								size="sm"
								assetTypeFields={latestAssetTypeFields}
								onSubmit={handleAssetTypeFieldsSubmit}
								hideCancel
							/>
						</>
					)}

					{procedureInitiatives.length > 0 && (
						<>
							<Separator size="full" />
							<RadixAccordian.Root className="flex flex-col gap-2" type="single" collapsible role="list">
								{procedureInitiatives.map((procedureInitiative) => (
									<BulkAssetControlProcedureInitiativeItem
										key={procedureInitiative.uuid}
										procedureInitiative={procedureInitiative}
										assetIds={assetIds}
									/>
								))}
							</RadixAccordian.Root>
						</>
					)}
				</RadixAccordian.Content>
			</Card>
		</RadixAccordian.Item>
	)
})
BulkAssetControlAssetTypeItem.displayName = "BulkAssetControlAssetTypeItem"
